import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Dialog,
  DialogButtons,
  DialogContent,
  DialogMessageBox,
  DialogTitle,
} from '@verantos-dev/shared-components';

import { downloadConceptGroupsWorkbook } from '../lib/api/downloadConceptGroupsWorkbook';
import type { ConceptGroupsExportModalProps } from '../lib/types/conceptGroupExport';

const DIALOG_PAPER_SX = { width: 600, maxHeight: '80%', margin: 0 } as const;

/**
 * Progress dialog for the concept-group workbook export. Mirrors `AdDownloadModal`
 * rather than reusing it — `feature-project-page` can't import that lib.
 */
export const ConceptGroupsExportModal = ({
  projectId,
  conceptGroupIds,
  onClose,
}: ConceptGroupsExportModalProps) => {
  const { formatMessage } = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingError, setSubmittingError] = useState<Error | null>(null);
  // Bumping this re-runs the download effect — wired to the error-state Retry.
  const [attempt, setAttempt] = useState(0);

  // Kept current without being a download-effect dependency, so a parent re-render
  // can't re-fire the one-shot download.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Keyed on the ids' contents, not the array's identity: a manifest refetch hands
  // down an equal array, which would re-fire the export from the error state.
  const conceptGroupIdsKey = conceptGroupIds.join(',');

  // The fetch is never aborted: closing only stops this component touching state
  // (`disposed`) — the download keeps running and the file still saves.
  useEffect(() => {
    if (!projectId) return undefined;

    let disposed = false;
    setIsSubmitting(true);
    setSubmittingError(null);

    downloadConceptGroupsWorkbook({ projectId, conceptGroupIds })
      .then(() => {
        if (!disposed) onCloseRef.current();
      })
      .catch((err: unknown) => {
        if (!disposed) setSubmittingError(err instanceof Error ? err : new Error('Export failed'));
      })
      .finally(() => {
        if (!disposed) setIsSubmitting(false);
      });

    return () => {
      disposed = true;
    };
  }, [projectId, conceptGroupIdsKey, attempt]);

  const onRetry = useCallback(() => setAttempt((n) => n + 1), []);

  const inError = !!submittingError && !isSubmitting;

  // A download is non-destructive, so the dialog is always dismissible.
  return (
    <Dialog
      id="conceptGroupsExportModal"
      open
      PaperProps={{ sx: DIALOG_PAPER_SX }}
      fullWidth
      maxWidth="lg"
      onClose={onClose}
    >
      <DialogTitle id="concept-groups-export-modal-title" showClose onClose={onClose}>
        {formatMessage({ id: 'conceptGroups.export.modalTitle' })}
      </DialogTitle>
      <DialogContent>
        {inError ? (
          <DialogMessageBox
            id="concept-groups-export-modal-error"
            title={formatMessage({ id: 'conceptGroups.export.errorTitle' })}
            type="error"
            text={formatMessage({ id: 'conceptGroups.export.errorText' })}
          />
        ) : (
          <DialogMessageBox
            id="concept-groups-export-modal-loading"
            title={formatMessage({ id: 'conceptGroups.export.loadingTitle' })}
            type="loading"
            text={formatMessage({ id: 'conceptGroups.export.loadingText' })}
          />
        )}
      </DialogContent>
      {inError && (
        <DialogButtons
          id="concept-groups-export-modal-buttons"
          saveButton={{
            label: formatMessage({ id: 'conceptGroups.export.retry' }),
            onClick: onRetry,
          }}
        />
      )}
    </Dialog>
  );
};
